import { Button, NativeSelect } from '@mantine/core';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import type { Problem } from '@/api';
import { isMutable, muteKey, problemCopy, problemKey } from '@/components/Problems/problemCopy';
import { EmptyText } from '@/components/Ui/EmptyText';
import { NavCount } from '@/components/Ui/NavCount';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Section } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { listTime } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from './Problems.module.css';

type Status = 'loading' | 'ready' | 'error';

export function ProblemsScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split('-')[0];
  const notify = useUndoToast();
  const formsSummary = useRustyState((s) => s.formsSummary);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const [params, setParams] = useSearchParams();
  const formId = params.get('form') ?? '';
  const [showMuted, setShowMuted] = useState(false);
  const filterId = useId();

  useEffect(() => {
    let ignore = false;
    const { api, refreshFormsSummary } = useRustyState.getState();
    void refreshFormsSummary();
    api
      .getProblems({ include_muted: true }, { onError: false })
      .then((res) => {
        if (ignore) return;
        const rows = res.data ?? [];
        setProblems(rows);
        setStatus('ready');
        // The sidebar badge counts the same rows; this list is the fresher of the two.
        useRustyState.setState({
          problemsCount: rows.filter((p) => p.group === 'action' && !p.muted).length,
        });
      })
      .catch(() => {
        if (!ignore) setStatus('error');
      });
    return () => {
      ignore = true;
    };
  }, []);

  const formOptions = formsSummary
    ? formsSummary.map((f) => ({ value: f.id, label: f.title }))
    : [
        ...new Map(
          problems
            .filter((p) => p.form_id)
            .map((p) => [p.form_id as string, p.form_title ?? (p.form_id as string)])
        ),
      ].map(([value, label]) => ({ value, label }));
  formOptions.sort((a, b) => a.label.localeCompare(b.label));

  const inForm = problems.filter((p) => !formId || p.form_id === formId);
  const mutedCount = inForm.filter((p) => p.muted).length;
  const visible = inForm.filter((p) => showMuted || !p.muted);
  const action = visible.filter((p) => p.group === 'action');
  const waiting = visible.filter((p) => p.group === 'waiting');
  const ours = visible.filter((p) => p.group === 'ours');
  const actionCount = action.filter((p) => !p.muted).length;

  const setFormId = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('form', value);
    else next.delete('form');
    setParams(next, { replace: true });
  };

  const setMuted = async (p: Problem, muted: boolean) => {
    const { api, refreshProblemsCount } = useRustyState.getState();
    const body = { form_id: p.form_id as string, cause: muteKey(p) };
    const key = problemKey(p);
    const apply = (value: boolean) =>
      setProblems((prev) => prev.map((x) => (problemKey(x) === key ? { ...x, muted: value } : x)));
    apply(muted);
    try {
      await (muted ? api.muteProblem(body) : api.unmuteProblem(body));
    } catch {
      apply(!muted);
      return;
    }
    void refreshProblemsCount();
    const form = p.form_title ?? t('problems.account');
    notify(
      t(muted ? 'problems.mutedToast' : 'problems.unmutedToast', { form }),
      () => void setMuted({ ...p, muted }, !muted)
    );
  };

  const formCell = (p: Problem) =>
    p.form_id ? (
      <UiLink to={`/account/forms/${p.form_id}`}>
        <strong>{p.form_title ?? p.form_id}</strong>
      </UiLink>
    ) : (
      <strong>{t('problems.account')}</strong>
    );

  return (
    <Page>
      <PageHeader title={t('problems.pageTitle')} subtitle={t('problems.intro')} />

      <div className={classes.toolbar}>
        <label htmlFor={filterId} className={classes.toolbarLabel}>
          {t('problems.formFilter')}
        </label>
        <NativeSelect
          id={filterId}
          value={formId}
          onChange={(e) => setFormId(e.currentTarget.value)}
          data={[{ value: '', label: t('problems.allForms') }, ...formOptions]}
        />
        <span className={classes.spacer} />
        {(mutedCount > 0 || showMuted) && (
          <Button size="xs" variant="subtle" onClick={() => setShowMuted((v) => !v)}>
            {showMuted ? t('problems.hideMuted') : t('problems.showMuted', { count: mutedCount })}
          </Button>
        )}
      </div>

      {status === 'error' && <EmptyText>{t('problems.loadFailed')}</EmptyText>}

      <Section
        title={
          <span className={classes.toolbar}>
            {t('problems.actionTitle')}
            {actionCount > 0 && <NavCount variant="risk">{actionCount}</NavCount>}
          </span>
        }
      >
        {status === 'loading' ? (
          <div className={classes.empty}>{t('ui.loading')}</div>
        ) : action.length === 0 ? (
          <div className={classes.empty}>{t('problems.actionEmpty')}</div>
        ) : (
          <>
            <div className={`${classes.row} ${classes.head}`} aria-hidden="true">
              <span>{t('problems.colForm')}</span>
              <span>{t('problems.colWhat')}</span>
              <span>{t('problems.colLastSeen')}</span>
              <span />
            </div>
            {action.map((p) => (
              <ActionRow
                key={problemKey(p)}
                problem={p}
                formCell={formCell(p)}
                locale={locale}
                onMute={setMuted}
              />
            ))}
          </>
        )}
      </Section>

      {waiting.length > 0 && (
        <Section title={t('problems.waitingTitle')} subtitle={t('problems.waitingSubtitle')}>
          {waiting.map((p) => (
            <div key={problemKey(p)} className={classes.row}>
              {formCell(p)}
              <span className={classes.sub}>{problemCopy(t, p, locale).body}</span>
              <span className={classes.sub}>
                {p.since ? t('problems.sinceLabel', { when: listTime(p.since, locale) }) : ''}
              </span>
              <span />
            </div>
          ))}
        </Section>
      )}

      {ours.length > 0 && (
        <Section title={t('problems.oursTitle')} subtitle={t('problems.oursSubtitle')}>
          {ours.map((p) => {
            const copy = problemCopy(t, p, locale);
            return (
              <div key={problemKey(p)} className={classes.row}>
                {formCell(p)}
                <span className={classes.sub}>{copy.body}</span>
                <span className={classes.sub}>
                  {p.last_seen ? listTime(p.last_seen, locale) : ''}
                </span>
                <span className={classes.actions}>
                  {copy.fix && <FixButton to={copy.fix.to} label={copy.fix.label} quiet />}
                </span>
              </div>
            );
          })}
        </Section>
      )}

      <p className={classes.footer}>{t('problems.footer')}</p>
    </Page>
  );
}

function FixButton({ to, label, quiet }: { to: string; label: string; quiet?: boolean }) {
  const { createLanguageURL } = useLanguageAwareRouting();
  return (
    <Button
      component={Link}
      to={createLanguageURL(to)}
      size="xs"
      variant={quiet ? 'subtle' : 'default'}
    >
      {label}
    </Button>
  );
}

function ActionRow({
  problem: p,
  formCell,
  locale,
  onMute,
}: {
  problem: Problem;
  formCell: ReactNode;
  locale: string;
  onMute: (p: Problem, muted: boolean) => void;
}) {
  const { t } = useTranslation();
  const copy = problemCopy(t, p, locale);
  const form = p.form_title ?? t('problems.account');
  const seen = p.last_seen ?? p.since;

  return (
    <div className={p.muted ? `${classes.row} ${classes.muted}` : classes.row}>
      {formCell}
      <div className={classes.what}>
        <strong>
          {copy.title}
          {p.muted && (
            <>
              {' '}
              <Tag>{t('problems.mutedTag')}</Tag>
            </>
          )}
        </strong>
        <span className={classes.sub}>{copy.body}</span>
      </div>
      <span className={classes.sub}>{seen ? listTime(seen, locale) : ''}</span>
      <span className={classes.actions}>
        {copy.fix && <FixButton to={copy.fix.to} label={copy.fix.label} />}
        {isMutable(p) && (
          <Button
            size="xs"
            variant="subtle"
            aria-label={t(p.muted ? 'problems.unmuteLabel' : 'problems.muteLabel', { form })}
            onClick={() => onMute(p, !p.muted)}
          >
            {t(p.muted ? 'problems.unmute' : 'problems.mute')}
          </Button>
        )}
      </span>
    </div>
  );
}
